import React, { useEffect, useRef, useState } from 'react';
import { FaWhatsapp, FaChevronLeft, FaChevronRight, FaCalendarAlt, FaRocket } from 'react-icons/fa';

import eventLogo from '../assets/ourdo-tail-curve-icon.svg';
import '../styles.css';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitch from '../components/LanguageSwitch';

type LocalizedText = {
  id: string;
  en: string;
};

type FeaturePackage = {
  name: string;
  badge: LocalizedText;
  description: LocalizedText;
  features: LocalizedText[];
};

const servicePackages: FeaturePackage[] = [
  {
    name: 'Our Event Basic',
    badge: { id: 'Komunitas', en: 'Community' },
    description: {
      id: 'Solusi praktis untuk acara komunitas berskala kecil.',
      en: 'Practical solution for small-scale community events.',
    },
    features: [
      { id: 'Moderator acara profesional', en: 'Professional event moderator' },
      { id: 'Poster digital siap pakai', en: 'Ready-to-use digital posters' },
      { id: 'Background virtual standar', en: 'Standard virtual background' },
      { id: 'Rundown sederhana', en: 'Simple event rundown' },
    ],
  },
  {
    name: 'Our Event Lite',
    badge: { id: 'Populer', en: 'Popular' },
    description: {
      id: 'Ideal untuk webinar komunitas atau pelatihan internal.',
      en: 'Ideal for community webinars or internal training.',
    },
    features: [
      { id: 'Semua layanan Basic', en: 'Everything in Basic' },
      { id: 'Undangan digital dengan RSVP', en: 'Digital invitation with RSVP' },
      { id: 'Rundown terperinci', en: 'Detailed event rundown' },
    ],
  },
  {
    name: 'Our Event Standard',
    badge: { id: 'Acara Menengah', en: 'Mid-scale Events' },
    description: {
      id: 'Pilihan tepat untuk acara organisasi dengan target peserta luas.',
      en: 'Perfect option for organisational events with broader audiences.',
    },
    features: [
      { id: 'Semua layanan Lite', en: 'Everything in Lite' },
      { id: 'Tiket digital dengan QR code', en: 'Digital tickets with QR codes' },
      { id: 'Statistik kehadiran peserta', en: 'Attendance analytics dashboard' },
    ],
  },
  {
    name: 'Our Event Pro',
    badge: { id: 'Profesional', en: 'Professional' },
    description: {
      id: 'Dirancang untuk pelatihan intensif atau seminar profesional.',
      en: 'Designed for intensive trainings or professional seminars.',
    },
    features: [
      { id: 'Semua layanan Standard', en: 'Everything in Standard' },
      { id: 'Sertifikat digital otomatis', en: 'Automated digital certificates' },
      { id: 'Streaming simultan ke YouTube', en: 'Simultaneous streaming to YouTube' },
    ],
  },
  {
    name: 'Our Event Premium',
    badge: { id: 'Konferensi', en: 'Conference' },
    description: {
      id: 'Sempurna untuk konferensi skala besar dan acara formal.',
      en: 'Perfect for large-scale conferences and formal events.',
    },
    features: [
      { id: 'Semua layanan Pro', en: 'Everything in Pro' },
      { id: 'Desain premium poster & undangan', en: 'Premium poster & invitation design' },
      { id: 'Highlight video pasca acara', en: 'Post-event highlight video' },
    ],
  },
  {
    name: 'Our Event Custom',
    badge: { id: 'Fleksibel', en: 'Flexible' },
    description: {
      id: 'Susun layanan sesuai kebutuhan dan anggaran acara Anda.',
      en: 'Compose services tailored to your event needs and budget.',
    },
    features: [
      { id: 'Paket layanan sesuai permintaan', en: 'Build-your-own service bundle' },
      {
        id: 'Pilihan sertifikat, tiket, undangan, poster, moderator, rundown, dokumentasi',
        en: 'Options for certificates, tickets, invitations, posters, moderators, rundowns, documentation',
      },
      { id: 'Penawaran khusus untuk acara edukasi', en: 'Special offers for educational programmes' },
    ],
  },
];

const whyChoose: LocalizedText[] = [
  {
    id: 'Fleksibel untuk berbagai skala acara, dari komunitas kecil hingga konferensi besar.',
    en: 'Flexible for event scales ranging from small communities to large conferences.',
  },
  {
    id: 'Tim profesional memastikan kelancaran acara dari pra-produksi hingga pasca acara.',
    en: 'Professional team ensures smooth delivery from pre-production to post-event.',
  },
  {
    id: 'Spesialisasi pada acara edukasi dengan layanan dan penawaran terbaik.',
    en: 'Specialised in education-focused events with dedicated services and offers.',
  },
];

const OurEventPage: React.FC = () => {
  const { language } = useLanguage();
  const isEnglish = language === 'en';
  const getText = (text: LocalizedText) => (isEnglish ? text.en : text.id);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [showWhyChoose, setShowWhyChoose] = useState(false);

  const checkScrollPosition = () => {
    const container = scrollRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setIsAtStart(scrollLeft === 0);
    setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -320, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 320, behavior: 'smooth' });
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    checkScrollPosition();
    container.addEventListener('scroll', checkScrollPosition);
    return () => container.removeEventListener('scroll', checkScrollPosition);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowWhyChoose(entry.isIntersecting),
      { threshold: 0.15 },
    );
    const target = document.querySelector('#why-choose');
    if (target) observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex justify-end">
          <LanguageSwitch />
        </div>

        <header className="mb-12 flex flex-col items-center gap-8 rounded-3xl border border-blue-100 bg-white p-10 shadow-xl md:flex-row md:items-start">
          <img
            src={eventLogo}
            alt="Our Event Logo"
            className="h-32 w-32 rounded-full border-4 border-white shadow-lg md:h-40 md:w-40"
          />
          <div className="space-y-4 text-center md:text-left">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
              {isEnglish ? 'Our Event Service' : 'Layanan Our Event'}
            </span>
            <h1 className="text-4xl font-black text-dark-blue md:text-5xl">
              Our Event
            </h1>
            <p className="text-lg text-gray-700 md:max-w-xl">
              {isEnglish
                ? 'Your trusted partner to deliver memorable events, from concept to completion.'
                : 'Mitra terpercaya untuk menyelenggarakan acara berkesan, mulai dari konsep hingga eksekusi tuntas.'}
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-700 md:justify-start">
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="text-blue-500" />
                {isEnglish ? 'End-to-end planning' : 'Perencanaan menyeluruh'}
              </div>
              <div className="flex items-center gap-2">
                <FaRocket className="text-blue-500" />
                {isEnglish ? 'Impact-focused delivery' : 'Eksekusi berfokus dampak'}
              </div>
            </div>
          </div>
        </header>

        <section className="relative mb-16">
          <h2 className="mb-8 text-center text-3xl font-bold text-dark-blue md:text-4xl">
            {isEnglish ? 'Service Packages' : 'Paket Layanan'}
          </h2>

          {!isAtStart && (
            <button
              type="button"
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/80 p-3 text-dark-blue shadow-lg transition hover:bg-white md:block"
            >
              <FaChevronLeft />
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scroll-smooth pb-4"
          >
            {servicePackages.map((pkg) => (
              <div
                key={pkg.name}
                className="min-w-[320px] rounded-3xl bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 p-1 shadow-lg transition hover:-translate-y-2 hover:shadow-2xl"
              >
                <div className="flex h-full flex-col rounded-3xl border border-blue-100 bg-white p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-dark-blue">{pkg.name}</h3>
                    <span className="rounded-full bg-accent-soft px-3 py-1 text-xs font-semibold text-accent">
                      {getText(pkg.badge)}
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-gray-600">{getText(pkg.description)}</p>
                  <ul className="mt-6 space-y-3 text-sm text-gray-700">
                    {pkg.features.map((feature) => (
                      <li key={feature.id} className="flex items-start gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-accent" />
                        <span>{getText(feature)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-6 text-center text-xs text-gray-500">
                    {isEnglish ? 'Best for teams needing quick setup.' : 'Ideal bagi tim yang membutuhkan eksekusi cepat.'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!isAtEnd && (
            <button
              type="button"
              onClick={scrollRight}
              className="absolute right-0 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/80 p-3 text-dark-blue shadow-lg transition hover:bg-white md:block"
            >
              <FaChevronRight />
            </button>
          )}
        </section>

        <section
          id="why-choose"
          className={`rounded-3xl bg-white p-8 shadow-xl transition-all duration-500 ${
            showWhyChoose ? 'opacity-100 translate-y-0' : 'translate-y-10 opacity-0'
          }`}
        >
          <div className="text-center">
            <h2 className="text-3xl font-bold text-dark-blue md:text-4xl">
              {isEnglish ? 'Why Teams Choose Our Event' : 'Mengapa Memilih Our Event'}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600">
              {isEnglish
                ? 'We combine solid operations with empathetic storytelling so every participant feels engaged.'
                : 'Kami memadukan operasi yang solid dengan storytelling empatik sehingga setiap peserta merasa terlibat.'}
            </p>
          </div>
          <ul className="mt-8 space-y-4 text-sm text-gray-700 md:text-base">
            {whyChoose.map((item) => (
              <li key={item.id} className="rounded-2xl border border-blue-100 bg-white px-4 py-3 shadow-sm">
                {getText(item)}
              </li>
            ))}
          </ul>
        </section>

        <div className="mt-16 text-center">
          <a
            href="https://wa.me/6282151155507"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-white shadow-lg transition duration-300 hover:bg-dark-accent"
          >
            <FaWhatsapp className="text-lg" />
            {isEnglish ? 'Talk with Our Event specialists' : 'Terhubung dengan spesialis Our Event'}
          </a>
          <p className="mt-4 text-xs text-gray-500">
            {isEnglish
              ? 'We respond within one business day with tailored recommendations.'
              : 'Kami akan merespons dalam satu hari kerja dengan rekomendasi yang relevan.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OurEventPage;
